<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    persistent
    @hide="onDialogHide"
  >
    <q-card
      class="q-pa-sm"
    >
      <q-card-section class="row">
        <div
          v-if="hasSessionNoteFieldId"
          class="text-h6"
        >
          {{ $t('sessions.noteTemplate.editSessionNoteField.textTitleEdit') }}
        </div>
        <div
          v-else
          class="text-h6"
        >
          {{ $t('sessions.noteTemplate.editSessionNoteField.textTitleAdd') }}
        </div>
        <q-space />
        <div v-if="!isSessionDataField">
          <q-btn
            flat
            round
            dense
            icon="more_vert"
          >
            <q-menu>
              <q-list>
                <q-item clickable>
                  <q-item-section>
                    <q-toggle
                      v-model="exposeInAPI"
                      :label="$t('common.toggleExposeInAPILabel')"
                    />
                  </q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-btn>
        </div>
        <div>
          <q-btn
            v-close-popup
            flat
            round
            dense
            icon="close"
          />
        </div>
      </q-card-section>
      <q-card-section class="q-py-none">
        <banner
          :message="$t('sessions.noteTemplate.editSessionNoteField.textTitleHint')"
        />
      </q-card-section>
      <q-form
        @submit="onSaveSessionNoteField"
      >
        <q-card-section
          class="row q-col-gutter-sm"
        >
          <q-input
            v-model="editSessionNoteField.label"
            outlined
            data-e2e="input-field-label"
            :rules="rules.label"
            hide-bottom-space
            hide-hint
            :label="$t('sessions.noteTemplate.editSessionNoteField.inputLabel')"
            :hint="$t('sessions.noteTemplate.editSessionNoteField.inputLabelHint')"
            class="col-12"
          />
          <q-input
            v-if="exposeInAPI"
            v-model="editSessionNoteField.apiFieldIdentifier"
            :rules="rules.apiIdentifier"
            :label="$t('common.inputAPIIdentifierLabel')"
            :hint="$t('common.inputAPIIdentifierHint')"
            hide-hint
            lazy-rules
            outlined
            hide-bottom-space
            class="col-12"
          />
          <q-input
            v-model="editSessionNoteField.hint"
            outlined
            :label="$t('sessions.noteTemplate.editSessionNoteField.inputFieldHint')"
            :hint="$t('sessions.noteTemplate.editSessionNoteField.inputFieldHintHint')"
            hide-hint
            hide-bottom-space
            class="col-12"
          />
          <q-select
            v-model="editSessionNoteField.type"
            :options="availableFieldTypeOptions"
            outlined
            data-e2e="dropdown-field-type"
            :label="$t('sessions.noteTemplate.editSessionNoteField.selectFieldType')"
            emit-value
            map-options
            hide-bottom-space
            :rules="rules.type"
            class="col-12"
            @update:model-value="onTypeChanged"
          />
          <q-select
            v-if="hasOptions"
            v-model="editSessionNoteField.options"
            outlined
            use-chips
            use-input
            multiple
            stack-label
            new-value-mode="add-unique"
            hide-dropdown-icon
            :label="$t('sessions.noteTemplate.editSessionNoteField.selectOptions')"
            :hint="$t('sessions.noteTemplate.editSessionNoteField.selectOptionsHint')"
            :rules="rules.options"
            class="col-12"
          />
          <template
            v-if="isScoreField"
          >
            <q-select
              v-model="editSessionNoteField.scoreConfig.scoreType"
              :options="availableScoreTypeOptions"
              map-options
              emit-value
              outlined
              :label="$t('sessions.noteTemplate.editSessionNoteField.selectScoreType')"
              :rules="rules.scoreType"
              class="col-12"
            />
            <template v-if="editSessionNoteField.scoreConfig?.scoreType === 'linearScale'">
              <div class="row col-12 items-center q-col-gutter-sm">
                <div class="col-auto">
                  <q-select
                    v-model="editSessionNoteField.scoreConfig.scoreRange.min.value"
                    :options="availableScoreTypeMinOptions"
                    map-options
                    emit-value
                    outlined
                    dense
                  />
                </div>
                <div class="col">
                  <q-input
                    v-model="editSessionNoteField.scoreConfig.scoreRange.min.label"
                    :label="$t('sessions.noteTemplate.editSessionNoteField.inputScoreTypeLabelOptional')"
                    dense
                  />
                </div>
              </div>
              <span class="col-12 q-ml-lg">{{ $t('common.to') }}</span>
              <div class="row col-12 items-center q-col-gutter-sm">
                <div class="col-auto">
                  <q-select
                    v-model="editSessionNoteField.scoreConfig.scoreRange.max.value"
                    :options="availableScoreTypeMaxOptions"
                    map-options
                    emit-value
                    outlined
                    dense
                  />
                </div>
                <div class="col">
                  <q-input
                    v-model="editSessionNoteField.scoreConfig.scoreRange.max.label"
                    :label="$t('sessions.noteTemplate.editSessionNoteField.inputScoreTypeLabelOptional')"
                    dense
                  />
                </div>
              </div>
            </template>
            <template v-else-if="editSessionNoteField.scoreConfig?.scoreType === 'explicit'">
              <div
                v-for="(explicitValue, idx) in editSessionNoteField.scoreConfig.explicitValues"
                :key="explicitValue.value"
                class="col-12 row"
              >
                <q-select
                  v-model="explicitValue.value"
                  :options="getAvailableScoreTypeOptions(explicitValue.value)"
                  map-options
                  emit-value
                  outlined
                  dense
                  class="col-auto q-mr-md"
                />
                <q-input
                  v-model="explicitValue.label"
                  :label="$t('sessions.noteTemplate.editSessionNoteField.inputScoreTypeLabel')"
                  dense
                  :rules="[(val) => !!val?.trim() || $t('common.validation.requiredField')]"
                  hide-bottom-space
                  class="col"
                />
                <q-btn
                  icon="close"
                  flat
                  dense
                  color="negative"
                  :disable="editSessionNoteField.scoreConfig?.explicitValues?.length === 1"
                  @click="onDeleteExplicitValue(idx)"
                />
              </div>
              <div>
                <q-btn
                  :label="$t('common.buttonAdd')"
                  icon="add"
                  flat
                  :disable="isAddExplicitValueDisabled"
                  @click="onAddExplicitValue"
                />
              </div>
            </template>
          </template>
          <q-select
            v-if="isLinkedToProgramField"
            v-model="editSessionNoteField.programField"
            :options="availableProgramFields"
            map-options
            emit-value
            outlined
            :label="$t('sessions.noteTemplate.editSessionNoteField.selectProgramField')"
            :rules="rules.programField"
            class="col-12"
          />
          <q-select
            v-if="isLinkedToClientField"
            v-model="editSessionNoteField.clientField"
            :options="availableClientFields"
            map-options
            emit-value
            outlined
            :label="$t('sessions.noteTemplate.editSessionNoteField.selectClientField')"
            :rules="rules.clientField"
            class="col-12"
          />
          <q-checkbox
            v-if="isLinkedToClientField"
            v-model="syncField"
            :label="$t('sessions.noteTemplate.editSessionNoteField.checkboxSyncField')"
            class="col-12"
          />
          <q-checkbox
            v-if="hasOptions"
            v-model="editSessionNoteField.allowMultipleOptions"
            :label="$t('sessions.noteTemplate.editSessionNoteField.checkboxAllowMultipleOptions')"
            class="col-12"
          />
          <q-checkbox
            v-if="isTextField"
            v-model="editSessionNoteField.allowMultiLines"
            :label="$t('sessions.noteTemplate.editSessionNoteField.checkboxAllowMultiLines')"
            class="col-12"
          />
          <template
            v-if="isAITextField"
          >
            <div
              v-if="!isSubscriptionPremium"
              class="col-12"
            >
              <premium-banner no-action />
            </div>
            <q-input
              v-else
              v-model="editSessionNoteField.aiInstructions"
              data-e2e="input-ai-instructions"
              :label="$t('sessions.noteTemplate.editSessionNoteField.inputAIInstructions')"
              :hint="$t('sessions.noteTemplate.editSessionNoteField.inputAIInstructionsHint')"
              autogrow
              outlined
              hide-bottom-space
              :rules="rules.aiInstructions"
              class="col-12"
            />
          </template>
          <q-select
            v-if="showListOutputFormat"
            v-model="editSessionNoteField.outputFormat"
            :label="$t('sessions.noteTemplate.editSessionNoteField.selectOutputFormat')"
            :hint="$t('sessions.noteTemplate.editSessionNoteField.selectOutputFormatHint')"
            outlined
            :options="[
              {
                value: null,
                label: $t('sessions.noteTemplate.editSessionNoteField.optionCommaSeparated')
              },
              {
                value: '<ul>{{#%TOKEN%}}<li>{{.}}</li>{{/%TOKEN%}}</ul>',
                label: $t('sessions.noteTemplate.editSessionNoteField.optionBulletpoints')
              },
            ]"
            emit-value
            map-options
            class="col-12"
          />
          <template v-if="isSessionDataField">
            <div class="col-12">
              <div class="text-subtitle2 text-dark-variant q-pt-md">
                {{ $t('sessions.noteTemplate.editSessionNoteField.sessionDataContentHeading') }}
              </div>
            </div>
            <q-checkbox
              v-model="editSessionNoteField.showSessionData"
              :label="$t('sessions.noteTemplate.editSessionNoteField.checkboxShowSessionData')"
              class="col-12"
            />
            <template v-if="editSessionNoteField.showSessionData">
              <q-select
                v-model="editSessionNoteField.sessionDataColumns"
                :label="$t('sessions.noteTemplate.editSessionNoteField.inputTargetColumns')"
                outlined
                :options="sessionDataOptions"
                emit-value
                map-options
                use-chips
                multiple
                class="col-12"
              />
            </template>
            <q-checkbox
              v-model="editSessionNoteField.showProgramNotes"
              :label="$t('sessions.noteTemplate.editSessionNoteField.checkboxShowProgramNotes')"
              class="col-12"
            />
            <q-checkbox
              v-model="editSessionNoteField.showSupervisorNotes"
              :label="$t('sessions.noteTemplate.editSessionNoteField.checkboxShowSupervisorNotes')"
              class="col-12"
            />
          </template>
          <div
            v-if="canRequireField || !hasSessionNoteFieldId"
            class="col-12"
          >
            <q-separator />
          </div>
          <q-checkbox
            v-if="canRequireField"
            v-model="editSessionNoteField.required"
            :label="$t('sessions.noteTemplate.editSessionNoteField.checkboxRequired')"
            class="col-12"
          />
          <template v-if="!hasSessionNoteFieldId">
            <q-checkbox
              :model-value="saveToLibrary"
              :label="$t('sessions.noteTemplate.editSessionNoteField.checkboxSave')"
              class="col-12"
              @update:model-value="onInputSaveToLibrary"
            />
            <q-select
              v-if="saveToLibrary"
              v-model="editSessionNoteField.category"
              :options="categoryList"
              outlined
              :label="$t('sessions.noteTemplate.editSessionNoteField.selectCategory')"
              use-input
              new-value-mode="add-unique"
              emit-value
              map-options
              hide-bottom-space
              :rules="rules.category"
              class="col-12"
              @new-value="onAddCategory"
            />
          </template>
        </q-card-section>
        <q-card-actions class="row">
          <q-btn
            v-if="canDelete"
            flat
            :label="$t('common.buttonDelete')"
            color="secondary"
            :loading="savingSpinner"
            @click="onDelete"
          />
          <q-btn
            v-if="canPreview"
            flat
            :label="$t('sessions.noteTemplate.editSessionNoteField.buttonPreviewForm')"
            color="dark-variant"
            @click="onPreviewField"
          />
          <q-space />
          <q-btn
            v-close-popup
            flat
            :label="$t('common.buttonCancel')"
            color="dark-variant"
          />
          <q-btn
            flat
            :label="$t('common.buttonSave')"
            :loading="savingSpinner"
            type="submit"
            color="primary"
            data-e2e="button-save"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>
<script>
import PreviewSessionNoteFieldForm from 'components/Sessions/SessionNoteTemplates/PreviewSessionNoteFieldForm';
import Banner from 'components/UI/Banner';
import PremiumBanner from 'components/UI/PremiumBanner';
import { chain, cloneDeep, concat, get, isEmpty } from 'lodash';
import { mapState } from 'pinia';
import { uid } from 'quasar';
import SessionNoteFieldsDB from 'src/fb/sessionNoteFields-db';
import ClientsMixin from 'src/mixins/ClientsMixin';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';
import { dataFields } from 'src/store/dataFields';

export default {
  name: 'EditSessionNoteField',

  components: { Banner, PremiumBanner },

  mixins: [ClientsMixin, Interactions],

  props: {
    categories: {
      type: Array,
      required: true,
    },

    sessionNoteField: {
      type: Object,
      required: false,
      default: null,
    },

    limitFieldTypes: {
      type: Array,
      default: () => ([]),
    },
  },

  emits: ['hide', 'ok'],

  data() {
    return {
      savingSpinner: false,

      saveToLibrary: false,
      newCategory: null,

      editSessionNoteField: {
        id: null,
        clinic: null,
        label: null,
        category: null,
        hint: null,
        type: 'text',
        options: null,
        allowMultipleOptions: false,
        allowMultiLines: false,
        showSessionData: false,
        showProgramNotes: false,
        showSupervisorNotes: false,
        sessionDataColumns: [],
        required: false,
        outputFormat: null,
      },

      fieldTypeOptions: [
        {
          value: 'text',
          label: this.$t('sessions.noteTemplate.editSessionNoteField.optionText'),
        },
        {
          value: 'aiText',
          label: this.$t('sessions.noteTemplate.editSessionNoteField.optionAIText'),
          requiresPremium: true,
          disablePreview: true,
        },
        {
          value: 'number',
          label: this.$t('sessions.noteTemplate.editSessionNoteField.optionNumber'),
        },
        {
          value: 'score',
          label: this.$t('sessions.noteTemplate.editSessionNoteField.optionScore'),
        },
        {
          value: 'select',
          label: this.$t('sessions.noteTemplate.editSessionNoteField.optionSelection'),
        },
        {
          value: 'date',
          label: this.$t('sessions.noteTemplate.editSessionNoteField.optionDate'),
        },
        {
          value: 'clientField',
          label: this.$t('sessions.noteTemplate.editSessionNoteField.optionClientField'),
          hint: this.$t('sessions.noteTemplate.editSessionNoteField.optionClientFieldDataHint'),
        },
        {
          value: 'programField',
          label: this.$t('sessions.noteTemplate.editSessionNoteField.optionProgramField'),
          hint: this.$t('sessions.noteTemplate.editSessionNoteField.optionProgramFieldHint'),
          disablePreview: true,
        },
        {
          value: 'sessionData',
          label: this.$t('sessions.noteTemplate.editSessionNoteField.optionSessionData'),
          hint: this.$t('sessions.noteTemplate.editSessionNoteField.optionSessionDataHint'),
          disablePreview: true,
        },
      ],

      sessionDataOptions: [
        { value: 'discriminativeStimulus', label: this.$t('programs.targets.properties.discriminativeStimulus') },
        { value: 'introductionDate', label: this.$t('programs.targets.properties.introductionDate') },
        { value: 'masteryDate', label: this.$t('programs.targets.properties.masteryDate') },
        { value: 'trialsToAcquire', label: this.$t('programs.targets.properties.trialsToAcquire') },
        { value: 'sessionsToAcquire', label: this.$t('programs.targets.properties.sessionsToAcquire') },
        { value: 'sessionTrials', label: this.$t('sessions.noteTemplate.editSessionNoteField.sessionTrialsLabel') },
        { value: 'sessionOutcome', label: this.$t('sessions.noteTemplate.editSessionNoteField.sessionOutcomeLabel') },
      ],

      rules: {
        apiIdentifier: [
          (val) => (!this.editSessionNoteField.exposeInAPI || !!val)
              || this.$t('common.inputAPIIdentifierEmptyError'),
          (val) => (!this.editSessionNoteField.exposeInAPI || /^[A-Za-z][A-Za-z0-9_]*$/.test(val))
              || this.$t('common.inputAPIIdentifierError'),
        ],
        category: [(val) => !!val || this.$t('sessions.noteTemplate.editSessionNoteField.selectCategoryValidator')],
        label: [
          (val) => !!val
              || this.$t('sessions.noteTemplate.editSessionNoteField.inputFieldLabelValidator'),
          (val) => (val.indexOf('.') === -1)
              || this.$t('sessions.noteTemplate.editSessionNoteField.inputFieldLabelDotValidator'),
        ],
        options: [(val) => (Array.isArray(val) ? val.length : !!val) || this.$t('common.validationRequired')],
        type: [
          (val) => !!val || this.$t('sessions.noteTemplate.editSessionNoteField.selectFieldTypeValidator'),
          (val) => (
            !chain(this.fieldTypeOptions).find({ value: val }).get('requiresPremium', false).value()
              || this.isSubscriptionPremium
          ) || this.$t('sessions.noteTemplate.editSessionNoteField.selectFieldTypeRequiresPremium'),
        ],
        scoreType: [
          (val) => !!val || this.$t('common.validation.requiredField'),
        ],
        clientField: [
          (val) => !!val || this.$t('sessions.noteTemplate.editSessionNoteField.selectFieldValidator'),
        ],
        clientDataField: [
          (val) => !!val || this.$t('sessions.noteTemplate.editSessionNoteField.selectFieldValidator'),
        ],
        programField: [
          (val) => !!val || this.$t('sessions.noteTemplate.editSessionNoteField.selectFieldValidator'),
        ],
        aiInstructions: [
          (val) => !!val || this.$t('sessions.noteTemplate.editSessionNoteField.inputAIInstructionsValidator'),
        ],
      },
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'isSubscriptionPremium']),
    ...mapState(dataFields, ['clientDataFields', 'visibleProgramDataFields']),

    hasSessionNoteFieldId() {
      return (get(this.editSessionNoteField, 'id', null) !== null);
    }, // hasSessionNoteFieldId()

    canExposeInAPI() {
      return this.hasSessionNoteFieldId && (this.editSessionNoteField.category !== null);
    },

    canRequireField() {
      return get(this.editSessionNoteField, 'type') !== 'sessionData';
    },

    canDelete() {
      return this.hasSessionNoteFieldId && (this.editSessionNoteField.category !== null);
    },

    canPreview() {
      if (!this.editSessionNoteField?.type) {
        return false;
      }

      return !chain(this.fieldTypeOptions)
        .find({ value: this.editSessionNoteField?.type })
        .get('disablePreview', false)
        .value();
    },

    isLinkedToClientField() {
      return get(this.editSessionNoteField, 'type') === 'clientField';
    }, // isLinkedToClientField()

    isLinkedToProgramField() {
      return get(this.editSessionNoteField, 'type') === 'programField';
    }, // isLinkedToProgramField()

    hasOptions() {
      return get(this.editSessionNoteField, 'type') === 'select';
    }, // hasOptions()

    isTextField() {
      return get(this.editSessionNoteField, 'type') === 'text';
    }, // isTextField()

    isAITextField() {
      return get(this.editSessionNoteField, 'type') === 'aiText';
    }, // isAITextField()

    isScoreField() {
      return get(this.editSessionNoteField, 'type') === 'score';
    }, // isScoreField()

    isAddExplicitValueDisabled() {
      if (!this.editSessionNoteField.scoreConfig?.explicitValues) {
        return true;
      }
      return this.editSessionNoteField.scoreConfig.explicitValues.length >= this.availableScoreExplicitOptions.length;
    }, // isAddExplicitValueDisabled()

    showListOutputFormat() {
      return (this.hasOptions && get(this.editSessionNoteField, 'allowMultipleOptions'))
        || this.isLinkedToProgramField;
    }, // showListOutputFormat()

    isSessionDataField() {
      return get(this.editSessionNoteField, 'type') === 'sessionData';
    },

    availableFieldTypeOptions() {
      if (!isEmpty(this.limitFieldTypes)) {
        return chain(this.fieldTypeOptions)
          .filter((fieldTypeOption) => this.limitFieldTypes.includes(fieldTypeOption.value))
          .value();
      }
      return this.fieldTypeOptions;
    },

    categoryList() {
      if (this.newCategory === null) {
        return this.categories;
      }
      return concat(this.categories, { value: this.newCategory, label: this.newCategory });
    }, // categoryList()

    availableScoreTypeOptions() {
      return [
        {
          value: 'linearScale',
          label: this.$t('sessions.noteTemplate.editSessionNoteField.selectScoreTypeOptions.linearScale'),
        },
        {
          value: 'explicit',
          label: this.$t('sessions.noteTemplate.editSessionNoteField.selectScoreTypeOptions.explicit'),
        },
      ];
    }, // availableScoreTypeOptions()

    availableScoreTypeMinOptions() {
      if (this.editSessionNoteField?.scoreConfig?.scoreType !== 'linearScale') {
        return [];
      }
      const max = this.editSessionNoteField.scoreConfig?.scoreRange?.max?.value ?? 10;
      return Array.from({ length: max }, (_, i) => ({
        value: i,
        label: i,
      }));
    }, // availableScoreTypeMinOptions()

    availableScoreTypeMaxOptions() {
      if (this.editSessionNoteField?.scoreConfig?.scoreType !== 'linearScale') {
        return [];
      }
      const min = this.editSessionNoteField.scoreConfig?.scoreRange?.min?.value ?? 0;
      return Array.from({ length: 10 - min }, (_, i) => ({
        value: min + i + 1,
        label: min + i + 1,
      }));
    }, // availableScoreTypeMaxOptions()

    availableScoreExplicitOptions() {
      const options = [{ value: null, label: '' }];
      for (let i = 0; i <= 10; i += 1) {
        options.push({ value: i, label: i });
      }
      return options;
    }, // availableScoreExplicitOptions()

    availableProgramFields() {
      return chain(this.visibleProgramDataFields)
        .filter((programDataField) => (['select'].includes(programDataField.type)))
        .map((programDataField) => ({
          value: programDataField.id,
          label: programDataField.label,
        }))
        .value();
    }, // availableProgramFields()

    availableClientFields() {
      return chain(this.clientColumns)
        .filter((column) => (chain(column).get('profileTypes').includes('client').value()))
        .filter((column) => (['text', 'select'].includes(column.type)))
        .map((clientColumn) => ({
          value: clientColumn.field,
          label: clientColumn.label,
        }))
        .concat(
          chain(this.clientDataFields)
            .filter((clientDataField) => (['text', 'select', 'date', 'yesNo'].includes(clientDataField.type)))
            .map((clientDataField) => ({
              value: `customData.${clientDataField.id}`,
              label: clientDataField.label,
            }))
            .value(),
        )
        .value();
    }, // availableClientFields()

    syncField: {
      get() {
        return get(this.editSessionNoteField, 'syncField', false);
      },
      set(value) {
        this.editSessionNoteField.syncField = value;
      },
    }, // exposeInAPI()

    exposeInAPI: {
      get() {
        return get(this.editSessionNoteField, 'exposeInAPI', false);
      },
      set(value) {
        this.editSessionNoteField.exposeInAPI = value;
      },
    }, // exposeInAPI()

  },

  watch: {
    sessionNoteField: {
      immediate: true,
      deep: true,
      handler(newSessionNoteField) {
        if (get(newSessionNoteField, 'id')) {
          this.editSessionNoteField = cloneDeep(newSessionNoteField);
        } else {
          this.editSessionNoteField = {
            id: null,
            clinic: null,
            label: null,
            category: null,
            hint: null,
            type: null,
            options: null,
            scoreConfig: null,
            allowMultipleOptions: false,
            allowMultiLines: false,
            showSessionData: false,
            showProgramNotes: false,
            showSupervisorNotes: false,
            sessionDataColumns: [],
            required: false,
            outputFormat: null,
          };
        }
        this.saveToLibrary = (get(this.editSessionNoteField, 'category', null) !== null);
      },
    },
  },

  methods: {

    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    }, // onDialogHide()

    async onSaveSessionNoteField() {
      this.savingSpinner = true;
      try {
        this.editSessionNoteField.clinic = this.userDetails.clinic;
        if (this.editSessionNoteField.category !== null) {
          // Save field for use across templates
          const sessionNoteFieldsDb = new SessionNoteFieldsDB();
          if (this.editSessionNoteField.id === null) {
            const newSessionNoteField = await sessionNoteFieldsDb.create(this.editSessionNoteField);
            this.editSessionNoteField.id = get(newSessionNoteField, 'id');
          } else {
            await sessionNoteFieldsDb.set(this.editSessionNoteField, this.editSessionNoteField.id);
          }
        } else if (this.editSessionNoteField.id === null) {
          // Save field to local template only
          this.editSessionNoteField.id = uid();
        }
        this.$emit('ok', this.editSessionNoteField);
        this.hide();
      } finally {
        this.savingSpinner = false;
      }
    }, // onSaveSessionNoteField()

    onTypeChanged(type) {
      // Session Data Table values aren't scalar, so they can't be exposed to Lumary's customFields.
      if (type === 'sessionData') {
        this.editSessionNoteField.exposeInAPI = false;
      }

      if (type !== 'score') {
        this.editSessionNoteField.scoreConfig = null;
        return;
      }

      if (this.editSessionNoteField.scoreConfig) {
        return;
      }

      this.editSessionNoteField.scoreConfig = {
        scoreType: 'linearScale',
        scoreRange: {
          min: {
            value: 1,
            label: '',
          },
          max: {
            value: 5,
            label: '',
          },
        },
        explicitValues: [
          {
            value: 0,
            label: '',
          },
        ],
      };
    }, // onTypeChanged()

    onInputSaveToLibrary(saveToLibrary) {
      this.saveToLibrary = saveToLibrary;
      if (!saveToLibrary) {
        this.editSessionNoteField.category = null;
      }
    },

    onAddCategory(label, done) {
      this.newCategory = label;
      done(label);
    }, // onAddCategory()

    async onDelete() {
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('sessions.noteTemplate.editSessionNoteField.notifyDeleteFieldConfirm'),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          if (!this.editSessionNoteField.id) {
            return;
          }
          try {
            this.savingSpinner = true;
            const sessionNoteFieldsDb = new SessionNoteFieldsDB();
            await sessionNoteFieldsDb.delete(this.editSessionNoteField.id);
            this.$emit('ok', null);
            this.hide();
            this.notifySuccess(this.$t('sessions.noteTemplate.editSessionNoteField.notifyDeleteFieldSuccess'));
          } catch (err) {
            this.notifyNegative(this.$t(
              'sessions.noteTemplate.editSessionNoteField.notifyDeleteFieldError',
              { error: err.message },
            ));
          } finally {
            this.savingSpinner = false;
          }
        });
    }, // onDelete()

    onPreviewField() {
      this.openComponentDialog(
        PreviewSessionNoteFieldForm,
        { sessionNoteField: this.editSessionNoteField },
      );
    }, // onPreviewField()

    onAddExplicitValue() {
      // Find next available value after last explicit value or starting from 0
      const lastValue = this.editSessionNoteField.scoreConfig.explicitValues.slice(-1)[0]?.value ?? -1;
      const availableGreaterOptions = this.availableScoreExplicitOptions
        .filter((option) => option.value > lastValue
          && !this.editSessionNoteField.scoreConfig.explicitValues
            .find((explicitValue) => explicitValue.value === option.value));

      if (availableGreaterOptions.length > 0) {
        const nextValue = availableGreaterOptions[0].value;
        this.editSessionNoteField.scoreConfig.explicitValues.push({ value: nextValue, label: '' });
        return;
      }

      const availableSmallerOptions = this.availableScoreExplicitOptions
        .filter((option) => option.value < lastValue
          && !this.editSessionNoteField.scoreConfig.explicitValues
            .find((explicitValue) => explicitValue.value === option.value));

      if (availableSmallerOptions.length > 0) {
        const nextValue = availableSmallerOptions[0].value;
        this.editSessionNoteField.scoreConfig.explicitValues.push({ value: nextValue, label: '' });
      }
    }, // onAddExplicitValue()

    onDeleteExplicitValue(index) {
      this.editSessionNoteField.scoreConfig.explicitValues.splice(index, 1);
    }, // onDeleteExplicitValue()

    getAvailableScoreTypeOptions(val) {
      if (this.editSessionNoteField.scoreConfig.scoreType !== 'explicit') {
        return [];
      }

      return this.availableScoreExplicitOptions
        .filter((option) => option.value === val
          || !this.editSessionNoteField.scoreConfig.explicitValues
            .find((explicitValue) => explicitValue.value === option.value));
    }, // getAvailableScoreTypeOptions()

  },
};
</script>
